import React from "react";
import { Card, Button, Row, Col, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { DeviceStatus } from "./DeviceDetails";
import { getCountBadge } from "../utils/getCountBadge";
import { getStatusBadge } from "../utils/getStatusBadge";
import { getReminderVariant } from "../utils/getReminderVariant";
import { FaBell } from "react-icons/fa";
import { useState } from "react";

// Typ urządzenia
export interface Device {
  id: number;
  name: string;
  description: string;
  avatar: string | null;
  returnable: 0 | 1;
  status: DeviceStatus;
  count: number;
  category: string; // Lokalizacja
  type: string;
  assignedTo?: {
    id: number;
    name: string;
    surname: string;
  } | null;
  reminders?: {
    id: number;
    id_user: number;
    id_device: number;
    reminder_date: string;
    description: string;
  }[];
}

type DeviceCardProps = {
  data: Device;
};

const DeviceCard = ({ data }: DeviceCardProps) => {
  const navigate = useNavigate();
  const [isImageLoading, setIsImageLoading] = useState(true);

  const isDisabled = data.status === DeviceStatus.InRepair || data.status === DeviceStatus.Broken;

  return (
    <Card className={`p-3 mb-2 ${isDisabled ? "isDisabled" : ""}`}>
      <Row className="align-items-center">
        <Col xs={4}>
          <div style={{ position: "relative" }}>
            {isImageLoading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                }}
              >
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}
            <Card.Img
              src={data.avatar ? data.avatar : "../assets/img/device.png"}
              style={{
                maxWidth: "200px",
                width: "100%",
                height: "auto",
                objectFit: "cover",
                borderRadius: "8px",
                border: data.assignedTo ? "5px solid #f4d457" : "",
                backgroundImage: `url(${data.avatar ? data.avatar : "../assets/img/device.png"})`,
                opacity: isImageLoading ? 0 : 1,
                transition: "opacity 0.3s ease-in-out",
              }}
              onLoad={() => setIsImageLoading(false)}
              onError={() => setIsImageLoading(false)}
              loading="lazy"
            />
          </div>
          <Card.Text>
            <Badge className="m-1" bg="secondary">
              Lokalizacja: {data.category}
            </Badge>
          </Card.Text>
        </Col>
        <Col xs={8} className="text-end">
          <Card.Title>
            <b>{data.name}</b> - <small>{data.description}</small>
          </Card.Title>
          <Card.Text>
            {data.returnable ? (
              <>
                {getStatusBadge(data.status)}
                <Badge className="m-1" bg="success">
                  Stacjonarne
                </Badge>
              </>
            ) : (
              <>
                {getCountBadge(data.count)}
                <Badge className="m-1" bg="secondary">
                  Wydawka
                </Badge>
              </>
            )}
            {data.type && (
              <Badge className="m-1" bg="secondary">
                {data.type}
              </Badge>
            )}
            {data.reminders && data.reminders.length > 0 && (
              <Badge className="m-1" bg={getReminderVariant(data.reminders[0].reminder_date)}>
                <FaBell /> {data.reminders.length}
              </Badge>
            )}
          </Card.Text>

          {data.assignedTo && (
            <Card.Text>
              <strong>Przypisane do: </strong>
              {data.assignedTo.name} {data.assignedTo.surname}
            </Card.Text>
          )}

          <Button variant="primary" onClick={() => navigate(`/device/${data.id}`)}>
            Zobacz historię
          </Button>
        </Col>
      </Row>
    </Card>
  );
};

export default React.memo(DeviceCard);
